import React, { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { motion } from "framer-motion"
import {
    ChevronRight, ChevronDown, FileText, PlayCircle, BookOpen,
    Clock, Award, CheckCircle, Lock, Layout, ArrowLeft,
    MonitorPlay, Paperclip, ExternalLink,
    Users, Target, GraduationCap, LayoutDashboard
} from "lucide-react"
import {
    getAppCourseDetail,
    getAppCourseContents,
    getLessonsAttachs,
    fetchAppMe,
} from "@/api/kuizy"
import { useKuizyTheme } from "@/styles/kuizyTheme"
import { API_BASE } from "@/settings/master"
import LearnerLayout from "@/layout/LearnerLayout"
import ProfileDrawer from "@/components/learners/ProfileDrawer"
const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } }
const pop = { initial: { opacity: 0, scale: .96 }, animate: { opacity: 1, scale: 1 }, transition: { duration: .22 } }

export default function LearnerCoursePage() {
    const { courseId } = useParams()
    const nav = useNavigate()
    const { t, theme } = useKuizyTheme()

    // Course Data State
    const [course, setCourse] = useState(null)
    const [contents, setContents] = useState([])
    const [loading, setLoading] = useState(true)

    // User profile (for LearnerLayout)
    const [profile, setProfile] = useState(null)

    // --- Data Fetching: Course ---
    const fetchData = useCallback(async () => {
        setLoading(true)
        try {
            const [cRes, cntRes] = await Promise.all([
                getAppCourseDetail(courseId),
                getAppCourseContents(courseId),
            ])

            setCourse(cRes.data || {})
            const cData = Array.isArray(cntRes.data) ? cntRes.data : (cntRes.data?.data || [])
            setContents(cData)
        } catch (error) {
            console.error("Failed to load course data", error)
        } finally {
            setLoading(false)
        }
    }, [courseId])

    useEffect(() => {
        fetchData()
        window.scrollTo(0, 0)
    }, [fetchData])

    // --- Data Fetching: User & Org ---
    useEffect(() => {
        let ignore = false;
        (async () => {
            try {
                const me = await fetchAppMe();
                if (ignore) return;
                setProfile(me.data || {})
            }
            catch { if (!ignore) nav("/learn/login", { replace: true }); }
        })();
        return () => { ignore = true; };
    }, [nav]);

    if (loading && !course) {
        return (
            <div className={`min-h-screen grid place-items-center ${t.bg}`}>
                <div className="animate-pulse flex flex-col items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-primary/20" />
                </div>
            </div>
        )
    }

    if (!course && !loading) {
        return (
            <div className={`min-h-screen grid place-items-center ${t.bg}`}>
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-700">ไม่พบหลักสูตร</h2>
                    <button onClick={() => nav("/learn/home")} className="mt-4 text-primary hover:underline">กลับหน้าแรก</button>
                </div>
            </div>
        )
    }

    const bannerUrl = course?.logo_url ? course.logo_url : "https://placehold.co/1200x400/e2e8f0/94a3b8?text=Course+Image"

    const srcImage = (url) => {
        if (!url) return 'https://placehold.co/600x400/e2e8f0/94a3b8?text=No+Image'
        const isAbsolute = /^(blob:|data:|http)/.test(url)
        return isAbsolute ? url : `${API_BASE}${url}`
    }

    return (
        <LearnerLayout profile={profile} onProfileSaved={() => fetchAppMe().then(r => setProfile(r.data || {})).catch(() => { })}>
            <div className={`min-h-screen pb-20 ${t.bg}`}>

                <main className="max-w-6xl mx-auto px-4 pt-6">

                    {/* Breadcrumb / Back */}
                    <div className="mb-4">
                        <button onClick={() => nav("/learn/home")} className={`flex items-center gap-2 text-sm hover:underline ${t.subtext}`}>
                            <ArrowLeft size={16} /> กลับหน้าหลัก
                        </button>
                    </div>

                    {/* Hero / Banner */}
                    <motion.div {...fadeIn} className="relative rounded-2xl overflow-hidden shadow-lg mb-8 aspect-[21/9] md:aspect-[3/1]">
                        <img src={bannerUrl} alt={course?.course_name} className="absolute inset-0 w-full h-full" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                            <div className="flex items-center gap-2 text-sm md:text-base font-medium text-pink-300 mb-2">
                                <span className="bg-white/20 backdrop-blur px-2 py-0.5 rounded text-xs text-white">{course?.course_code || "CODE"}</span>
                            </div>
                            <h1 className="text-2xl md:text-4xl font-bold mb-3">{course?.course_name}</h1>
                            <p className="text-gray-200 text-sm md:text-base max-w-2xl line-clamp-2">{course?.description}</p>

                            <div className="flex flex-wrap gap-4 mt-6 text-sm text-gray-300">
                                <div className="flex items-center gap-1.5">
                                    <BookOpen size={16} className="text-pink-400" />
                                    <span>{contents.length} เนื้อหา</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Contents Grid */}
                    <div className="mb-12">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className={`text-xl font-bold ${t.text}`}>เนื้อหาทั้งหมด</h2>
                        </div>

                        {contents.length === 0 ? (
                            <div className={`p-8 rounded-2xl text-center border-dashed border-2 ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                                <div className="text-gray-400 mb-2">ยังไม่มีเนื้อหาในหลักสูตรนี้</div>
                            </div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                transition={{ duration: .35, delay: .1 }}
                                className="grid gap-6 md:gap-8 lg:gap-12 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
                            >
                                {contents.map(c => (
                                    <motion.div key={c.content_id} whileHover={{ y: -4 }}
                                        className="bg-white rounded-2xl shadow-md flex flex-col border border-gray-100">
                                        <div className="rounded-t-2xl overflow-hidden bg-neutral-100">
                                            <Link to={`/learn/course/${courseId}/content/${c.content_id}`}>
                                                <img
                                                    src={srcImage(c.logo_url)}
                                                    alt={c.name || c.content_name}
                                                    className="w-full h-48 object-cover"
                                                />
                                            </Link>
                                        </div>
                                        <div className='p-3 flex flex-col flex-1'>
                                            <div>
                                                <Link to={`/learn/course/${courseId}/content/${c.content_id}`} className="text-base font-semibold text-primary leading-snug hover:underline">
                                                    {c.name || c.content_name}
                                                </Link>
                                                <p className="text-xs mt-1 text-gray-500">
                                                    {c.code || c.content_code}
                                                </p>
                                            </div>
                                            <div className="line-clamp-2 text-[12px] mt-2 text-gray-600 flex-1">
                                                {c.description || "ไม่มีคำอธิบายเพิ่มเติม"}
                                            </div>

                                            {/* stats */}
                                            <div className="flex justify-between items-center pt-3 mt-3 border-t border-gray-100">
                                                <div className="flex items-center gap-2">
                                                    <div className="bg-secondary/10 p-1.5 rounded-md">
                                                        <BookOpen className="text-secondary" size={16} />
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] text-gray-500">บทเรียน</p>
                                                        <p className="font-semibold text-[12px]">{c.lesson_count || 0}</p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <div className="bg-secondary/10 p-1.5 rounded-md">
                                                        <FileText className="text-secondary" size={16} />
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] text-gray-500">ชุดข้อสอบ</p>
                                                        <p className="font-semibold text-[12px]">{c.test_count || 0}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}
                    </div>
                </main>

                {/* Modals & Drawers */}
                {/* <ProfileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} profile={profile} onSaved={() => {
                    // Refresh profile logic if needed
                    fetchAppMe().then(r => setProfile(r.data || {})).catch(() => { })
                }} /> */}
            </div>
        </LearnerLayout>
    )
}
